import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Breadcrumbs,
  Link,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const DynamicBreadcrumbs = () => {
  const location = useLocation();
  const theme = useTheme();

  // Split path and filter out MongoDB ObjectIds (24 hex chars)
  const pathnames = location.pathname
    .split('/')
    .filter((x) => x && !/^[a-f0-9]{24}$/.test(x));

  // Optional: You can add custom display names for specific routes
  const getFriendlyName = (value) => {
    const friendlyNames = {
      dashboard: 'Dashboard',
      profile: 'My Profile',
      'crime-search': 'Crime Search',
      register: 'Register Crime',
      'view-crime': 'Crime Details',
      admin: 'Administration',
      reports: 'Reports',
      // Add more project-specific route names as needed
    };

    return friendlyNames[value] || 
           value
             .replace(/-/g, ' ')
             .split(' ')
             .map(word => word.charAt(0).toUpperCase() + word.slice(1))
             .join(' ');
  };

  if (pathnames.length === 0) {
    return null; // Don't show breadcrumbs on homepage
  }

  return (
    <Box
      sx={{
        py: 1.5,
        px: { xs: 2, md: 3 },
        backgroundColor: theme.palette.mode === 'dark' 
          ? 'rgba(255,255,255,0.03)' 
          : 'rgba(0,0,0,0.02)',
        borderRadius: 1,
        mb: 3,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Breadcrumbs
        separator={
          <NavigateNextIcon 
            fontSize="small" 
            sx={{ color: 'text.secondary' }}
          />
        }
        aria-label="breadcrumb"
        maxItems={4}
        itemsBeforeCollapse={2}
        itemsAfterCollapse={2}
        sx={{
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
          },
        }}
      >
        <Link
          component={RouterLink}
          to="/"
          underline="hover"
          color="inherit"
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontWeight: 500,
            '&:hover': {
              color: theme.palette.primary.main,
            },
          }}
        >
          Home
        </Link>

        {pathnames.map((value, index) => {
          const to = `/${pathnames.slice(0, index + 1).join('/')}`;
          const isLast = index === pathnames.length - 1;
          const displayName = getFriendlyName(value);

          return isLast ? (
            <Typography
              key={to}
              color="text.primary"
              fontWeight="medium"
              sx={{
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayName}
            </Typography>
          ) : (
            <Link
              key={to}
              component={RouterLink}
              to={to}
              underline="hover"
              color="inherit"
              sx={{
                '&:hover': {
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                },
              }}
            >
              {displayName}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default DynamicBreadcrumbs;